import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";

type PackType = {
    id: string;
    packName: string;
    companyId?: string;
    createdAt: string;
    updatedAt: string;
};

function PackTypePage() {
    const [packTypes, setPackTypes] = useState<PackType[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const companyId = new URLSearchParams(window.location.search).get("companyId");
                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                queryParams.append("page", currentPage.toString());

                const res = await fetch(
                    `${import.meta.env.VITE_API_URL}/pack-types?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const data = await res.json();
                setPackTypes(Array.isArray(data?.data) ? data.data : []);
                setTotalPages(data.totalPages || 1);
            } catch (err) {
                console.error("Failed to fetch pack types:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage]);

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this pack type?")) return;

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/pack-types/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                setPackTypes(prev => prev.filter(pg => pg.id !== id));
            } else {
                const error = await res.json();
                alert(`Failed to delete pack type: ${error?.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Delete failed", err);
            alert("Something went wrong deleting product type");
        }
    };

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Pack Types
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let addUrl = "/pack-types/add";
                                        if (projectId) {
                                            addUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            addUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(addUrl);
                                    }}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Add New Pack Type
                                </Button>
                            </div>
                        </div>
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <div className="relative">
                                            <Lucide
                                                icon="Search"
                                                className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                            />
                                            <FormInput
                                                type="text"
                                                placeholder="Search pack types..."
                                                className="pl-9 sm:w-64 rounded-[0.5rem]"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Menu>
                                            <Menu.Button
                                                as={Button}
                                                variant="outline-secondary"
                                                className="w-full sm:w-auto"
                                            >
                                                <Lucide
                                                    icon="Download"
                                                    className="stroke-[1.3] w-4 h-4 mr-2"
                                                />
                                                Export
                                                <Lucide
                                                    icon="ChevronDown"
                                                    className="stroke-[1.3] w-4 h-4 ml-2"
                                                />
                                            </Menu.Button>
                                            <Menu.Items className="w-40">
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    PDF
                                                </Menu.Item>
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    CSV
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                    </div>
                                </div>
                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td className="py-1.5">Pack Type Name</Table.Td>
                                                <Table.Td className="py-1.5">Created At</Table.Td>
                                                <Table.Td className="py-1.5">Updated At</Table.Td>
                                                <Table.Td className="text-right py-1.5">Action</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {packTypes.map((pack) => (
                                                <Table.Tr key={pack.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {pack.packName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {new Date(pack.createdAt).toLocaleDateString()}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {new Date(pack.updatedAt).toLocaleDateString()}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-right border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-end items-center gap-3">
                                                            <Link
                                                                to={(() => {
                                                                    const searchParams = new URLSearchParams(window.location.search);
                                                                    const projectId = searchParams.get("projectId");
                                                                    const companyId = searchParams.get("companyId");
                                                                    
                                                                    let editUrl = `/pack-types/${pack.id}/edit`;
                                                                    if (projectId) {
                                                                        editUrl += `?projectId=${projectId}`;
                                                                    } else if (companyId) {
                                                                        editUrl += `?companyId=${companyId}`;
                                                                    }
                                                                    
                                                                    return editUrl;
                                                                })()}
                                                                className="btn btn-outline-secondary p-2 rounded-lg"
                                                                title="Edit"
                                                            >
                                                                <Lucide icon="Pencil" className="w-3 h-3" />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDelete(pack.id)}
                                                                className="btn btn-outline-danger p-2 rounded-lg"
                                                                title="Delete"
                                                            >
                                                                <Lucide icon="Trash2" className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>...</Pagination.Link>
                                        {Array.from({ length: totalPages }).map((_, i) => (
                                            <Pagination.Link
                                                key={i}
                                                active={currentPage === i + 1}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Link>
                                        ))}
                                        <Pagination.Link>
                                            <Lucide icon="ChevronRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect className="sm:w-20 rounded-[0.5rem]">
                                        <option>10</option>
                                        <option>25</option>
                                        <option>35</option>
                                        <option>50</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default PackTypePage;
